import type { WorkflowDefinition } from "../types";

/**
 * W10 — Track a rebrand in AI answers.
 *
 * `shared`: a rebrand happens once to a property, and everyone watching it is
 * watching the same transition. One checklist, continued by whoever is on it.
 *
 * The alias step is the one that saves the reporting and the easiest one to
 * skip in launch week: without it the old name stops counting on launch day
 * and the trend appears to collapse.
 */
export const rebrandWorkflow: WorkflowDefinition = {
	id: "rebrand",
	wNumber: 10,
	title: "Track a rebrand in AI answers",
	outcome:
		"Keep one continuous trend line through the name change, with a date you can point to.",
	audiences: ["pr", "inhouse"],
	uses: [
		"Brand Snapshot",
		"Settings",
		"Key Messages",
		"Annotations",
		"Messages",
		"Citation Intelligence",
	],
	scope: "property",
	concurrency: "shared",
	progress: "explicit",
	// A rebrand rewrites ground truth, which only a subject that owns its own
	// snapshot can do: BRAND, PRODUCT and PERSON. A LOCATION inherits the
	// parent's identity, and CATEGORY has no focal subject to rename.
	isApplicable: (mode) =>
		mode.isBrand || mode.isProduct || mode.subjectType === "PERSON",
	articleSlug: "how-to-track-a-rebrand-in-ai-answers",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "update-snapshot",
			title: "Update the brand profile before launch day",
			page: "Brand Snapshot",
			route: "/brand-snapshot",
			criterion: "You've edited the snapshot since you started.",
			why: "Leave the old profile in place and you measure a company that no longer exists. Your edits are kept when the snapshot is rebuilt, so you only do this once.",
			docsHref: "/docs/dashboards/brand-snapshot",
			tasks: [
				"Change the name, then the category, audience and differentiators",
				"Review who the brand is compared against now",
				"Do it the week before launch",
			],
			complete: { kind: "since", event: "snapshotEdited" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Fields you corrected",
							value: state.property.snapshotOverrideFieldCount,
							target: 1,
							caption:
								"your edits are kept when the snapshot is rebuilt",
						}
					: null,
		},
		{
			id: "alias",
			title: "Keep the old name as an alias",
			page: "Settings",
			route: "/settings?tab=general",
			criterion: "You've added at least one other name.",
			why: "AI will use the old name for months. Without an alias, visibility looks like it collapsed on launch day.",
			docsHref: "/docs/dashboards/settings",
			tasks: [
				"Add the previous name and the variants people really used",
				"Add folded-in product names and the legal entity name",
				"Ask an assistant whether the old and new company are the same",
			],
			// Count, not `since`: an alias is a row on the property with no
			// creation timestamp in the observed state, so there is nothing to
			// anchor against the run's start. Allowlisted in `catalog.test.ts`.
			complete: { kind: "count", key: "aliasCount", gte: 1 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Other names counted as yours",
							value: state.property.aliasCount,
							target: 1,
						}
					: null,
		},
		{
			id: "messages",
			title: "Turn the new positioning into key messages",
			page: "Key Messages",
			route: "/key-messages",
			criterion: "You've added a positive key message since you started.",
			why: "Most rebrands are about meaning something different, not about the name. Write that down and you can test whether it lands.",
			docsHref: "/docs/dashboards/key-messages",
			tasks: [
				"Write three to seven phrases, under ten words each",
				"Add the old descriptions as negative messages",
				"Expect slow movement at first, then faster",
			],
			// The new positioning is the point of the rebrand, so the messages
			// that count are the ones written for it — not whatever was on the
			// property before anyone opened this playbook.
			complete: { kind: "since", event: "positiveKeyMessageAdded" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: `${state.property.keyMessages.positive} new positioning messages`,
									done:
										state.property.keyMessages.positive > 0,
								},
								{
									label: `${state.property.keyMessages.negative} retired descriptions tracked`,
									done:
										state.property.keyMessages.negative > 0,
								},
							],
						}
					: null,
		},
		{
			id: "annotate-launch",
			title: "Mark launch day and every milestone",
			page: "Annotations",
			route: "/annotations",
			criterion: "You've noted a milestone since you started.",
			why: "The first month is noisy and three things happen the same week. Dated markers settle the argument six weeks later.",
			docsHref: "/docs/dashboards/annotations",
			tasks: [
				"Mark the announcement and the site cutover separately",
				"Mark the release, the major coverage and the analyst briefing",
				"Keep marking as coverage lands",
			],
			complete: { kind: "since", event: "annotationCreated" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Milestones on the timeline",
							value: state.property.manualAnnotationCount,
						}
					: null,
		},
		{
			id: "watch",
			title: "Watch the names swap over",
			page: "Messages",
			route: "/dashboard/messages",
			criterion: "You've got daily data for this brand.",
			why: "The date the old and new names cross is the most quotable number here. Read it weekly, because one day is noise.",
			docsHref: "/docs/dashboards/brand-dashboard",
			tasks: [
				"Track the old and new names side by side",
				"Read message pull-through beside it",
				"Expect a consistency dip in month one",
			],
			complete: { kind: "flag", key: "hasNightlyMetrics" },
			enrichment: "messageTrend",
			// The series comes from the message-tracking hook in the step panel;
			// the loader carries counts, never a time series.
			signal: () => null,
		},
		{
			id: "stale-sources",
			title: "Find the sources still telling the old story",
			page: "Messages",
			route: "/dashboard/messages",
			criterion: "You've worked the source list this month.",
			why: "When the trend stalls, the cause is always specific pages. The matched answers show which ones AI is still reading.",
			docsHref: "/docs/dashboards/key-messages",
			tasks: [
				"Open the matched answers for the old name",
				"Read the Sources tab for pages that have not been updated",
				"Start with the brand's own stray pages, then chase the rest",
			],
			requires: "messageMatches",
			// Manual on purpose: nothing watches "you worked the source list".
			complete: { kind: "manual" },
		},
	],
};
